import React, {memo, useState} from 'react';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import Image, {type StaticImageData} from 'next/image';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import {cdnImage} from '@/util/cdnImage';
import {borderRadius, colors, fontSize, fontWeight, gradients} from '@/styles/tokens';
import {imgSizes} from '@/util/imageSizes';

// Same photo used for Gurpreet Kaur in the "Our Leadership Team" section (about-us/index.ts `coreTeamData`).
const gurpreetKaurPresident = cdnImage('about-us-new/Gurpreet-kaur.webp');
const daljit = cdnImage('about-us-new/Daljit-Singh.webp');
const sonali = cdnImage('about-us-new/Sonali-Gupta.webp');
const gurmel = cdnImage('about-us-new/Gurmel-Singh.webp');
const gurpreet = cdnImage('about-us-new/adv-gurpreet.webp');

const members = [
    {
        image: gurpreetKaurPresident,
        name: 'Gurpreet Kaur',
        role: 'President',
        roleColor: colors.accent.poshBlue,
        email: 'gurpreet.k@debutinfotech.com'
    },
    {
        image: gurpreet,
        name: 'Adv. Gurpreet Kaur',
        role: 'External Member & POSH Trainer',
        roleColor: colors.accent.poshBlue,
        email: 'adgurpreet@gmail.com'
    },
    {
        image: daljit,
        name: 'Daljit Singh',
        role: 'Committee Member',
        roleColor: colors.accent.poshBlue,
        email: 'daljit.singh@debutinfotech.com'
    },
    {
        image: gurmel,
        name: 'Gurmel Singh',
        role: 'Committee Member',
        roleColor: colors.accent.poshBlue,
        email: 'admin@debutinfotech.com'
    },
    {
        image: sonali,
        name: 'Sonali Gupta',
        role: 'Committee Member',
        roleColor: colors.accent.poshBlue,
        email: 'sonali.gupta@debutinfotech.com'
    }
];

const MemberAvatar = ({src, name}: {src: StaticImageData; name: string}) => {
    const [imgError, setImgError] = useState(false);
    const initials = name
        .split(' ')
        .slice(0, 2)
        .map((n) => n[0])
        .join('')
        .toUpperCase();

    if (imgError) {
        return (
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: colors.accent.poshBlue,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <Typography sx={{color: '#fff', fontSize: fontSize[24], fontWeight: fontWeight.bold, lineHeight: 1}}>{initials}</Typography>
            </Box>
        );
    }

    // Avatar box is a fixed 64/80/140px circle — `sizes` must match or next/image
    // defaults to 100vw and fetches the 3840px variant for a thumbnail.
    return (
        <Image
            src={src}
            alt={name}
            fill
            sizes={imgSizes({xs: '64px', sm: '80px', lg: '140px'})}
            style={{objectFit: 'cover', objectPosition: 'top'}}
            onError={() => setImgError(true)}
        />
    );
};

const PoshSection = () => {
    return (
        <Box
            className="top-space mobile-top-padding bottom-spacing mobile-bottom-padding"
            component="section"
            sx={{background: gradients.banner}}
        >
            <MUIContainer>
                <Box textAlign="center" maxWidth={1024} margin="0 auto" mb={5}>
                    <Typography variant="h1" component="h2" mb={3} fontSize={{xs: fontSize[32], sm: fontSize[48]}} className="font-headers">
                        Building Safe Workplaces Through Regular Expert-Led POSH Awareness and Training
                    </Typography>
                    <Typography variant="subtitle1" component="p" className="font-regular-text" mb={2}>
                        At Debut Infotech, we believe a high-performing team can only thrive in a workplace where every individual feels
                        safe, respected, and heard. In line with the Sexual Harassment of Women at Workplace (Prevention, Prohibition and
                        Remediation) Act, 2013, we conduct regular POSH awareness sessions for all employees across our offices.
                    </Typography>
                    <Typography variant="subtitle1" component="p" className="font-regular-text" mb={2}>
                        These sessions are facilitated by Advocate Gurpreet Kaur, a legal expert specializing in workplace rights and
                        compliance. Each session covers employee rights under the POSH Act, the role and responsibilities of the Internal
                        Committee, reporting procedures, and what constitutes workplace harassment — ensuring every team member understands
                        both their protections and their responsibilities.
                    </Typography>
                    <Typography variant="subtitle1" component="p" className="font-regular-text">
                        Our commitment to POSH compliance is not a checkbox exercise. It is part of how we build a culture where
                        accountability is practiced, not just stated.
                    </Typography>
                </Box>

                {/* Member Cards */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: 'repeat(2, 1fr)',
                            sm: 'repeat(3, 1fr)',
                            lg: 'repeat(5, 1fr)'
                        },
                        gap: {xs: 1.5, sm: 2, lg: 3},
                        width: '100%',
                        boxSizing: 'border-box'
                    }}
                >
                    {members.map((member) => (
                        <Box
                            key={member.name}
                            sx={{
                                backgroundColor: colors.background.default,
                                borderRadius: borderRadius.xl,
                                p: {xs: 1.5, sm: 2, lg: 3},
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: {xs: 1.5, sm: 2},
                                border: `1px solid ${colors.neutral.borderLight}`,
                                overflow: 'hidden',
                                minWidth: 0,
                                boxSizing: 'border-box'
                            }}
                        >
                            {/* Circular photo */}
                            <Box
                                sx={{
                                    width: {xs: 64, sm: 80, lg: 140},
                                    height: {xs: 64, sm: 80, lg: 140},
                                    borderRadius: borderRadius.circle,
                                    overflow: 'hidden',
                                    position: 'relative',
                                    flexShrink: 0
                                }}
                            >
                                <MemberAvatar src={member.image} name={member.name} />
                            </Box>

                            <Box sx={{textAlign: 'center', width: '100%', minWidth: 0, overflow: 'hidden'}}>
                                <Typography
                                    className="font-headers"
                                    sx={{
                                        fontSize: fontSize[16],
                                        mb: 0.5,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {member.name}
                                </Typography>
                                <Typography
                                    className="font-regular-text"
                                    sx={{
                                        fontSize: {xs: fontSize[12], sm: fontSize[16], lg: fontSize[16]},
                                        color: member.roleColor,
                                        fontWeight: fontWeight.medium,
                                        lineHeight: 1.4,
                                        mb: 1.5
                                    }}
                                >
                                    {member.role}
                                </Typography>
                                <Link
                                    href={`mailto:${member.email}`}
                                    underline="none"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        width: '100%',
                                        justifyContent: 'center',
                                        px: {xs: 0.75, sm: 1},
                                        py: 0.75,
                                        borderRadius: borderRadius.md,
                                        border: `1px solid ${colors.neutral.borderLight}`,
                                        backgroundColor: colors.background.slate50,
                                        color: colors.text.muted,
                                        fontSize: fontSize[12],
                                        transition: 'all 0.2s ease',
                                        overflow: 'hidden',
                                        minWidth: 0,
                                        boxSizing: 'border-box',
                                        '&:hover': {
                                            backgroundColor: colors.background.tag,
                                            borderColor: colors.accent.poshBlue,
                                            color: colors.accent.poshBlue
                                        }
                                    }}
                                >
                                    <MailOutlineIcon sx={{fontSize: fontSize[12], flexShrink: 0}} />
                                    <Typography
                                        component="span"
                                        className="font-regular-text"
                                        sx={{
                                            fontSize: fontSize[12],
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            minWidth: 0
                                        }}
                                    >
                                        {member.email}
                                    </Typography>
                                </Link>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </MUIContainer>
        </Box>
    );
};

export default memo(PoshSection);
